import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { copilotAsk, copilotQuickActions, saveCopilotConversation, trackEvent } from "@/lib/api";
import {
  MessageSquare, X, Send, Sparkles, ChevronRight, Loader2, ExternalLink, Pin,
} from "lucide-react";
import { toast } from "sonner";
import FeedbackBar from "@/components/FeedbackBar";
import AnsweredByPill from "@/components/AnsweredByPill";

/**
 * Universal floating Copilot — mounts once at the app shell level and is
 * available on every authenticated page. Same backend (/api/copilot/ask) as
 * /app/copilot.
 */
export default function FloatingCopilot() {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState([]); // [{q, ...response}]
  const [actions, setActions] = useState([]);
  const location = useLocation();
  const params = useParams();
  const scrollRef = useRef(null);

  useEffect(() => {
    copilotQuickActions().then((r) => setActions(r.actions || [])).catch(() => {});
  }, []);

  // Auto-scroll on new response
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread]);

  const currentPage = () => {
    const path = location.pathname;
    if (path.startsWith("/app/gmail")) return "inbox";
    if (path.startsWith("/app/memories")) return "memories";
    if (path.startsWith("/app/tasks")) return "tasks";
    if (path.startsWith("/app/children/")) return "child-profile";
    if (path.startsWith("/app/children")) return "children";
    if (path.startsWith("/app/calendar")) return "calendar";
    if (path.startsWith("/app/briefing")) return "briefing";
    if (path.startsWith("/app/copilot")) return "copilot";
    if (path.startsWith("/app/settings")) return "settings";
    return "dashboard";
  };

  const ask = async (q) => {
    const text = (q ?? question).trim();
    if (!text) return;
    setBusy(true);
    const ctx = {
      page: currentPage(),
      child_id: params.childId || null,
    };
    try {
      const r = await copilotAsk(text, ctx);
      setThread((prev) => [...prev, { q: text, ...r }]);
      setQuestion("");
    } catch (e) {
      setThread((prev) => [
        ...prev,
        {
          q: text,
          answer: e?.response?.data?.detail || "Couldn't reach the Copilot just now.",
          sources: [],
          deeplinks: [],
          llm_available: false,
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    ask();
  };

  return (
    <>
      {!open && (
        <button
          data-testid="copilot-floating-btn"
          onClick={() => { setOpen(true); trackEvent("copilot_open", { source: "floating" }); }}
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-[var(--fcc-ink)] text-white shadow-2xl hover:opacity-90 transition"
          aria-label="Ask KidHQ Copilot"
        >
          <Sparkles className="w-4 h-4 text-[var(--fcc-emma)]" />
          <span className="text-sm font-semibold">Ask KidHQ</span>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="copilot-panel">
          <button
            type="button"
            aria-label="Close Copilot"
            onClick={() => setOpen(false)}
            className="flex-1 bg-[var(--fcc-ink)]/30 backdrop-blur-sm"
          />
          <aside className="w-full max-w-md sm:max-w-lg bg-white h-full shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <header className="p-5 border-b border-[var(--fcc-border)] flex items-start justify-between gap-3">
              <div>
                <p className="label-overline flex items-center gap-2">
                  <Sparkles className="w-3 h-3 text-[var(--fcc-emma)]" /> Parent Copilot
                </p>
                <h3 className="font-display text-xl text-[var(--fcc-ink)] mt-1">Ask anything about your family</h3>
                <p className="text-xs text-[var(--fcc-muted)] mt-1">Grounded in your real emails, tasks, calendar & memories.</p>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 hover:bg-[var(--fcc-cream-2)] rounded-xl" data-testid="copilot-close">
                <X className="w-4 h-4" />
              </button>
            </header>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-5">
              {thread.length === 0 && (
                <div data-testid="copilot-quick-actions" className="space-y-3">
                  <p className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]">Try one of these</p>
                  <ul className="space-y-2">
                    {actions.map((a, idx) => (
                      <li key={idx}>
                        <button
                          data-testid={`copilot-quick-action-${idx}`}
                          onClick={() => ask(a)}
                          className="w-full text-left rounded-xl border border-[var(--fcc-border)] px-3.5 py-2.5 text-sm font-semibold text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream-2)] flex items-center gap-2"
                        >
                          <Sparkles className="w-3.5 h-3.5 text-[var(--fcc-green)]" />
                          <span className="flex-1">{a}</span>
                          <ChevronRight className="w-3 h-3 text-[var(--fcc-muted)]" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {thread.map((turn, idx) => {
                const pinTurn = async () => {
                  if (turn._pinned) return;
                  turn._pinned = true;  // local de-dupe
                  try {
                    const title = turn.q.length > 80 ? turn.q.slice(0, 77) + "…" : turn.q;
                    const payload = {
                      title,
                      summary: (turn.answer || "").slice(0, 360),
                      conversation: [
                        { role: "user", content: turn.q },
                        { role: "assistant", content: turn.answer },
                      ],
                      child_id: params.childId || turn.matched_child?.id || null,
                      pinned: true,
                    };
                    await saveCopilotConversation(payload);
                    toast.success("Pinned to your family vault.");
                  } catch (e) {
                    toast.error("Couldn't pin this conversation.");
                  }
                };
                return (
                <article key={idx} data-testid="copilot-turn" className="space-y-3">
                  <div className="flex items-start gap-2 justify-end">
                    <p className="bg-[var(--fcc-emma)] text-[var(--fcc-ink)] rounded-2xl rounded-tr-md px-3.5 py-2 text-sm max-w-[85%]">
                      {turn.q}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-7 h-7 rounded-lg bg-[var(--fcc-green-soft)] flex items-center justify-center shrink-0">
                      <Sparkles className="w-3.5 h-3.5 text-[var(--fcc-green)]" />
                    </span>
                    <div className="flex-1 space-y-3">
                      <p data-testid="copilot-answer" className="text-sm text-[var(--fcc-ink)] leading-relaxed whitespace-pre-wrap">{turn.answer}</p>
                      {!turn.llm_available && turn.llm_error === "budget_exceeded" && (
                        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                          AI is over the Emergent LLM budget. Top up in Profile → Universal Key.
                        </p>
                      )}
                      {turn.deeplinks?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5" data-testid="copilot-deeplinks">
                          {turn.deeplinks.map((d, di) => (
                            <Link
                              key={di}
                              to={d.to}
                              data-testid={`copilot-deeplink-${di}`}
                              onClick={() => setOpen(false)}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-[var(--fcc-ink)] text-white hover:opacity-90"
                            >
                              {d.label} <ExternalLink className="w-2.5 h-2.5" />
                            </Link>
                          ))}
                          <button
                            onClick={pinTurn}
                            data-testid={`copilot-pin-${idx}`}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-white border border-[var(--fcc-border)] text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream-2)]"
                          >
                            <Pin className="w-2.5 h-2.5" /> Pin
                          </button>
                        </div>
                      )}
                      {turn.sources?.length > 0 && (
                        <details data-testid="copilot-sources" className="text-[11px] text-[var(--fcc-muted)]">
                          <summary className="cursor-pointer hover:text-[var(--fcc-ink)]">
                            Based on {turn.sources.length} source{turn.sources.length === 1 ? "" : "s"}
                          </summary>
                          <ul className="mt-2 space-y-1 pl-2">
                            {turn.sources.map((s, si) => (
                              <li key={si} className="truncate">· {s.label}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                      <FeedbackBar
                        surface="copilot"
                        targetId={`copilot-${idx}`}
                        compact
                        className="pt-1"
                      />
                      {turn.model_used && (
                        <AnsweredByPill model_used={turn.model_used} className="mt-1" />
                      )}
                    </div>
                  </div>
                </article>
                );
              })}

              {busy && (
                <div className="flex items-center gap-2 text-sm text-[var(--fcc-muted)]" data-testid="copilot-loading">
                  <Loader2 className="w-4 h-4 animate-spin" /> Thinking…
                </div>
              )}
            </div>

            <form onSubmit={onSubmit} className="p-4 border-t border-[var(--fcc-border)] bg-[var(--fcc-cream)]/60">
              <div className="flex gap-2">
                <input
                  data-testid="copilot-input"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="What should I focus on this week?"
                  className="fcc-input flex-1 text-sm"
                  disabled={busy}
                />
                <button
                  type="submit"
                  data-testid="copilot-submit"
                  disabled={busy || !question.trim()}
                  className="fcc-btn-primary text-sm py-2.5 px-4 inline-flex items-center gap-1.5"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-[10px] text-[var(--fcc-muted)] mt-2 text-center">
                <MessageSquare className="w-2.5 h-2.5 inline mr-1" />
                Open the full chat at <Link to="/app/copilot" className="text-[var(--fcc-green)] font-semibold" onClick={() => setOpen(false)}>/app/copilot</Link>
              </p>
            </form>
          </aside>
        </div>
      )}
    </>
  );
}
